<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-title>
              <AppPageHeader
                icon="mdi-file-tree-outline"
                title="Sub-Categories"
                subtitle="Finer groupings inside each category (e.g. Women > Kurtis)"
                style="width: 100%"
              >
                <v-btn
                  v-if="can('CATEGORIES.CREATE')"
                  fab
                  x-small
                  color="primary"
                  @click="openCreate"
                >
                  <v-icon dark>mdi-plus</v-icon>
                </v-btn>
              </AppPageHeader>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search ... "
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="clearSearch"
                    @keydown.enter="reload"
                  ></v-text-field>
                </v-col>
                <v-col sm="4">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.parent_id"
                    :items="categories"
                    item-text="name"
                    item-value="id"
                    clearable
                    label="Category"
                    prepend-inner-icon="mdi-folder-outline"
                    @change="reload"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </section>
              <v-simple-table v-else fixed-header height="500" class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-start">Sr No</th>
                      <th class="text-start">Sub-Category</th>
                      <th class="text-start">Category</th>
                      <th class="text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="subCategories.length">
                    <tr v-for="(row, i) in subCategories" :key="`sub_${row.id}`">
                      <td style="min-width: 85px">{{ (page - 1) * rowsPerPage + i + 1 }}</td>
                      <td>{{ row.name }}</td>
                      <td>
                        <v-chip small outlined color="primary">{{ parentPath(row) || "—" }}</v-chip>
                      </td>
                      <td class="text-right">
                        <v-tooltip bottom v-if="can('CATEGORIES.UPDATE')">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              class="mr-2"
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="primary"
                              @click="openEdit(row)"
                            >
                              <v-icon dark>mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit this Sub-Category</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="can('CATEGORIES.DELETE')">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="error"
                              @click="askDelete(row)"
                            >
                              <v-icon dark>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span>Delete this Sub-Category</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="4">
                        <AppEmptyState
                          icon="mdi-file-tree-outline"
                          title="No sub-categories yet"
                          message="Add a sub-category under a category to group products more finely."
                          :action-text="can('CATEGORIES.CREATE') ? 'Add Sub-Category' : ''"
                          @action="openCreate"
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination rounded v-model="page" :length="paginationLength"></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <ConfirmationDialog
      :show.sync="deleteDialog"
      title="Delete Sub-Category"
      :message="pendingDelete ? `Delete ${pendingDelete.name}? Products already in it keep their category.` : ''"
      icon="mdi-delete"
      color="error"
      :loading="isDeleting"
      @confirm="deleteRow"
    />

    <!-- Add / Edit Sub-Category -->
    <AppDialog
      :show.sync="showFormDialog"
      max-width="600"
      icon="mdi-file-tree-outline"
      :title="form.id ? 'Edit Sub-Category' : 'Add Sub-Category'"
      subtitle="Pick the category it belongs under"
    >
      <div class="pa-6">
        <v-row>
          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="form.parent_id"
              :items="categories"
              item-text="name"
              item-value="id"
              outlined
              prepend-inner-icon="mdi-folder-outline"
              :error-messages="errors.parent_id"
            >
              <template v-slot:label>Category <span style="color:#f44336;font-weight:bold;">*</span></template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.name"
              outlined
              prepend-inner-icon="mdi-card-text-outline"
              :error-messages="errors.name"
              @input="form.name = (form.name || '').toUpperCase()"
              @keydown.enter="save"
            >
              <template v-slot:label>Sub-Category Name <span style="color:#f44336;font-weight:bold;">*</span></template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-divider class="my-4"></v-divider>
        <v-btn text class="mr-2" @click="showFormDialog = false">Cancel</v-btn>
        <v-btn rounded color="primary" :loading="isSaving" :disabled="isSaving" @click="save">
          Save Sub-Category
        </v-btn>
      </div>
    </AppDialog>
  </v-main>
</template>

<script>
import axios from "axios";
import ConfirmationDialog from "@/components/ConfirmationDialog.vue";
import AppDialog from "@/components/AppDialog.vue";
import AppEmptyState from "@/components/AppEmptyState.vue";
import AppPageHeader from "@/components/AppPageHeader.vue";

export default {
  name: "SubCategoriesListPage",
  components: { ConfirmationDialog, AppDialog, AppEmptyState, AppPageHeader },
  data() {
    return {
      filter: { search_keyword: "", parent_id: null },
      categories: [],
      subCategories: [],
      page: 1,
      rowsPerPage: 10,
      paginationLength: 0,
      isLoading: false,
      showFormDialog: false,
      form: { id: null, name: "", parent_id: null },
      isSaving: false,
      deleteDialog: false,
      pendingDelete: null,
      isDeleting: false,
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getCategories();
    this.getData();
  },
  methods: {
    // Top-level categories - what a sub-category can sit under
    async getCategories() {
      try {
        const response = await axios.get("categories");
        this.categories = response.data.data || [];
      } catch (error) {
        console.error(error);
      }
    },
    async getData() {
      this.isLoading = true;
      const params = new URLSearchParams({
        sub_only: 1,
        page: this.page,
        rowsPerPage: this.rowsPerPage,
        search_keyword: this.filter.search_keyword || "",
      });
      if (this.filter.parent_id) params.append("parent_id", this.filter.parent_id);
      try {
        const response = await axios.get(`categories?${params.toString()}`);
        this.subCategories = response.data.data || [];
        this.paginationLength = Math.ceil((response.data.count || 0) / this.rowsPerPage);
      } catch (error) {
        console.error(error);
      }
      this.isLoading = false;
    },
    reload() {
      if (this.page !== 1) {
        this.page = 1; // the page watcher reloads
      } else {
        this.getData();
      }
    },
    clearSearch() {
      this.filter.search_keyword = "";
      this.reload();
    },
    // "WOMEN" or, for deeper levels, "WOMEN > KURTIS"
    parentPath(row) {
      const names = [];
      let parent = row.parent_category;
      while (parent) {
        names.unshift(parent.name);
        parent = parent.parent_category;
      }
      return names.join(" > ");
    },
    openCreate() {
      this.$store.dispatch("errors/setErrors", {});
      this.form = { id: null, name: "", parent_id: this.filter.parent_id || null };
      this.showFormDialog = true;
    },
    openEdit(row) {
      this.$store.dispatch("errors/setErrors", {});
      this.form = { id: row.id, name: row.name, parent_id: row.parent_id };
      this.showFormDialog = true;
    },
    async save() {
      if (!this.form.parent_id) {
        this.$store.dispatch("errors/setErrors", { parent_id: ["Choose the category this goes under."] });
        return;
      }
      this.isSaving = true;
      this.$store.dispatch("errors/setErrors", {});
      const payload = { name: this.form.name, parent_id: this.form.parent_id };
      try {
        if (this.form.id) {
          await axios.patch(`/categories/${this.encryptIt(String(this.form.id))}`, payload);
        } else {
          await axios.post("/categories", payload);
        }
        this.showFormDialog = false;
        if (this.$toast) this.$toast.success("Sub-category saved successfully");
        this.getData();
      } catch (error) {
        console.error(error);
        if (error.response && error.response.data && error.response.data.errors) {
          this.$store.dispatch("errors/setErrors", error.response.data.errors);
        }
      }
      this.isSaving = false;
    },
    askDelete(row) {
      this.pendingDelete = row;
      this.deleteDialog = true;
    },
    async deleteRow() {
      this.isDeleting = true;
      try {
        await axios.post(`/categories/delete/${this.encryptIt(String(this.pendingDelete.id))}`);
        this.deleteDialog = false;
        this.getData();
      } catch (error) {
        console.error(error);
      }
      this.isDeleting = false;
    },
  },
};
</script>
